import {ChangeDetectionStrategy, Component, input, viewChild} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Temporal} from "temporal-polyfill";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {MatDatepickerModule, MatDateRangePicker} from "@angular/material/datepicker";
import {MatPrefix} from "@angular/material/form-field";
import {TranslateModule} from "@ngx-translate/core";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {KlikkMedStopPropagationDirektiv} from "web-ng2/delt/direktiver/klikkMedStopPropagation/klikkMedStopPropagation";
import {filter, switchMap} from "rxjs";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {MatOption, MatSelect} from "@angular/material/select";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {MatDivider} from "@angular/material/divider";
import {deaktiverFormKontroller} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {MatTooltip} from "@angular/material/tooltip";
import PlainDate = Temporal.PlainDate;

const debug = initDebugger('VelgSøkeperiodeKomponent');

export type SkjemaType = ReturnType<typeof VelgSøkeperiodeKomponent.opprettSkjema>;
export type SkjemaVerdier = SkjemaType['value'];

type PredefinerteValg = 'iÅr' | 'iFjor' | 'siste1mnd' | 'siste3mnd' | 'siste6mnd' | 'rangePicker';

/**
 * Gui for å velge datoperiode et indeks-søk skal begrenses til.
 * Stilet som chip og ment for å brukes sammen med velg-søkbare-entiteter
 */
@Component({
    selector: 'velg-soekeperiode',
    templateUrl: 'velg-søkeperiode.html',
    styleUrl: 'velg-søkeperiode.scss',
    host: {
        '(click)': 'aktiver()'
    },
    imports: [MatDatepickerModule, ReactiveFormsModule, MatPrefix, TranslateModule, MatIcon, MatIconButton, KlikkMedStopPropagationDirektiv, MatSelect, MatOption, MatDivider, MatTooltip],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VelgSøkeperiodeKomponent {

    /** Oppretter nytt tomt skjema */
    static opprettSkjema() {
        return new FormGroup({
            start: new FormControl<PlainDate | null>(null, {nonNullable: true}),
            slutt: new FormControl<PlainDate | null>(null, {nonNullable: true}),
        });
    }

    /** Oppretter nytt skjema som spenner over inneværende år */
    static opprettSkjemaMedInneværendeÅrValgt(klokke: Klokke) {
        let nå = klokke.nåSomPlainDate();
        return new FormGroup({
            start: new FormControl<PlainDate | null>(nå.with({day: 1, month: 1}), {nonNullable: true}),
            slutt: new FormControl<PlainDate | null>(nå.with({day: 31, month: 12}), {nonNullable: true}),
        });
    }

    /** Oppretter nytt skjema som spenner over perioden nå - ett år -> nå + ett år */
    static opprettSkjemaMedEttÅrTilbakeOgEttÅrFrem(klokke: Klokke) {
        let nå = klokke.nåSomPlainDate();
        return new FormGroup({
            start: new FormControl<PlainDate | null>(nå.subtract({years: 1}), {nonNullable: true}),
            slutt: new FormControl<PlainDate | null>(nå.add({years: 1}), {nonNullable: true}),
        });
    }

    periodevelger = viewChild.required<MatDateRangePicker<any>>('periodevelger');
    predefinertPerioderValg = viewChild.required<MatSelect>('predefinertPerioderValg');

    skjemakontroll = input.required<SkjemaType>();

    predefinertPeriodeKontroll = new FormControl<PredefinerteValg | null>(null);

    constructor(private klokke: Klokke) {
        //når bruker velger predefinert periode oppdaterer vi fra-til datoer i skjema
        this.predefinertPeriodeKontroll.valueChanges.pipe(filter(erIkkeNil), takeUntilDestroyed()).subscribe(valgt =>
          this.predefinertValgt(valgt));

        //når skjemakontroll deaktiveres deaktiverer vi også predefinertPeriodeKontroll
        toObservable(this.skjemakontroll)
          .pipe(
            switchMap(kontroll => kontroll.statusChanges),
            takeUntilDestroyed())
          .subscribe(status => deaktiverFormKontroller(status == 'DISABLED', this.predefinertPeriodeKontroll, {emitEvent: false}));
    }

    aktiver() {
        this.skjemakontroll().enable();
    }

    toggle() {
        if (this.skjemakontroll().disabled)
            this.skjemakontroll().enable();
        else
            this.skjemakontroll().disable();
    }

    // private hjelpemetoder

    private predefinertValgt(predefinert: PredefinerteValg) {
        const nå = this.klokke.nåSomPlainDate()
        switch (predefinert) {
            case "iÅr":
                this.skjemakontroll().patchValue({start: nå.with({day: 1, month: 1}), slutt: nå.with({day: 31, month: 12})});
                break;
            case "iFjor":
                const januarIFjor = nå.with({day: 1, month: 1}).subtract({years: 1});
                this.skjemakontroll().patchValue({start: januarIFjor, slutt: januarIFjor.with({day: 31, month: 12})});
                break;
            case "siste1mnd":
                this.skjemakontroll().patchValue({start: nå.with({day: 1}).subtract({months: 1}), slutt: nå});
                break;
            case "siste3mnd":
                this.skjemakontroll().patchValue({start: nå.with({day: 1}).subtract({months: 3}), slutt: nå});
                break;
            case "siste6mnd":
                this.skjemakontroll().patchValue({start: nå.with({day: 1}).subtract({months: 6}), slutt: nå});
                break;

            case "rangePicker":
                this.periodevelger().open();
                break;
        }

        this.predefinertPeriodeKontroll.setValue(null, {emitEvent: false});
    }
}
